import { Delete, Edit as EditIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import {
  useDeleteFederationProviderMutation,
  useGetFederationProvidersQuery,
} from '../../../app/redux-fetch/apiQuery'
import AddButton from '../../../components/Buttons/AddButton'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../components/Typographies/TableTitleTypography'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import DomainBadge from './DomainBadge'
import EditFederationProviderModal from './EditFederationProviderModal'
import FederationProviderWizard from './FederationProviderWizard'
import messages from './messages'
import { FederationProviderListItem } from './types'

// E12 F12-5 (WI 633) — the account-owner Settings > Federation Providers list. Mirrors VerifiedDomainsTable:
// MRT grid + a right-side AddButton opening the wizard, a per-row Edit (domains) + Remove. All statuses are
// listed (including auto-disabled providers, whose failure reason shows in the expandable detail panel).
const FederationProvidersTable = () => {
  const { formatMessage } = useIntl()

  const { data: providers, isSuccess } = useGetFederationProvidersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const [deleteProvider] = useDeleteFederationProviderMutation()

  const [wizardOpen, setWizardOpen] = useState(false)
  const [toEdit, setToEdit] = useState<FederationProviderListItem | undefined>()
  const [toRemove, setToRemove] = useState<FederationProviderListItem | undefined>()

  // E20 F20-7 (WI 794): the consent return leg lands on /settings/federation?consentStateKey=… — capture
  // the key once, reopen the wizard on its consent step (the poll advances it), and strip the param so
  // closing the wizard doesn't reopen it on the next render.
  const [searchParams, setSearchParams] = useSearchParams()
  const [resumeConsentKey, setResumeConsentKey] = useState<string | undefined>()
  useEffect(() => {
    const key = searchParams.get('consentStateKey')
    if (!key) return
    setResumeConsentKey(key)
    setWizardOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('consentStateKey')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const closeWizard = () => {
    setWizardOpen(false)
    setResumeConsentKey(undefined)
  }

  const handleRemove = async () => {
    if (!toRemove) return
    const res = await deleteProvider({ id: toRemove.id })
    if ('error' in res && res.error) return // the base query already surfaces the error toast
    enqueueSnackbar(formatMessage(messages.providerRemoved), { variant: 'success' })
    setToRemove(undefined)
  }

  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: formatMessage(messages.name) },
      { accessorKey: 'providerType', header: formatMessage(messages.providerType) },
      {
        id: 'domains',
        header: formatMessage(messages.domains),
        enableSorting: false,
        Cell: ({ row }: { row: { original: FederationProviderListItem } }) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.original.domains.map((d) => (
              <DomainBadge key={d.id} domain={d} />
            ))}
          </Box>
        ),
      },
      {
        accessorKey: 'disabled',
        header: formatMessage(messages.status),
        Cell: ({ row }: { row: { original: FederationProviderListItem } }) =>
          row.original.disabled ? (
            <Chip size='small' color='error' label={formatMessage(messages.statusDisabled)} />
          ) : (
            <Chip size='small' color='success' label={formatMessage(messages.statusActive)} />
          ),
      },
      {
        accessorFn: (row: FederationProviderListItem) =>
          row.lastUpdate ? dayjs(row.lastUpdate).format('YYYY-MM-DD HH:mm') : '—',
        id: 'lastUpdate',
        header: formatMessage(messages.lastUpdate),
      },
    ],
    [formatMessage],
  )

  return (
    <>
      <SideModalWrapper
        open={wizardOpen}
        key='wizard'
        headerLabel={formatMessage(messages.wizardTitle)}
        onClose={closeWizard}
        withBottomBox={false}
      >
        <FederationProviderWizard
          key={resumeConsentKey ?? 'new'}
          onDone={closeWizard}
          initialStep={resumeConsentKey ? 1 : undefined}
          consentStateKey={resumeConsentKey}
        />
      </SideModalWrapper>

      <SideModalWrapper
        open={Boolean(toEdit)}
        key='edit'
        headerLabel={toEdit ? formatMessage(messages.editTitle, { name: toEdit.name }) : ''}
        onClose={() => setToEdit(undefined)}
        withBottomBox={false}
      >
        {toEdit && <EditFederationProviderModal provider={toEdit} />}
      </SideModalWrapper>

      <Dialog open={Boolean(toRemove)} onClose={() => setToRemove(undefined)}>
        <DialogTitle>{formatMessage(messages.confirmRemoveTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {formatMessage(messages.confirmRemoveBody, { name: toRemove?.name ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRemove(undefined)}>{formatMessage(messages.cancel)}</Button>
          <Button color='error' variant='contained' onClick={handleRemove}>
            {formatMessage(messages.remove)}
          </Button>
        </DialogActions>
      </Dialog>

      <MRTDataGridv2
        state={{ isLoading: !isSuccess }}
        initialState={{ sorting: [{ id: 'name', desc: false }] }}
        leftHeadingComponent={
          <Box sx={{ display: 'inline-flex' }}>
            <TableTitleTypography>{formatMessage(messages.title)}</TableTitleTypography>
          </Box>
        }
        rightHeadingComponent={
          <AddButton text={formatMessage(messages.addProvider)} onClick={() => setWizardOpen(true)} />
        }
        columns={columns}
        data={providers || []}
        getRowId={(row) => String(row.id)}
        enableRowActions
        positionActionsColumn='last'
        renderDetailPanel={({ row }: { row: { original: FederationProviderListItem } }) =>
          row.original.disabled && row.original.lastFailureReason ? (
            <Box sx={{ p: 1 }}>
              <Typography variant='body2' color='error'>
                {/* Plain text — React escapes it, so the server reason is XSS-safe. */}
                {formatMessage(messages.disabledReason, { reason: row.original.lastFailureReason })}
              </Typography>
            </Box>
          ) : null
        }
        renderRowActions={({ row }: { row: { original: FederationProviderListItem } }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton
              color='primary'
              aria-label={formatMessage(messages.edit)}
              onClick={() => setToEdit(row.original)}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              color='primary'
              aria-label={formatMessage(messages.remove)}
              onClick={() => setToRemove(row.original)}
            >
              <Delete />
            </IconButton>
          </Box>
        )}
      />
    </>
  )
}

export default FederationProvidersTable
